import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { History, Search, ChevronLeft, ChevronRight, Plus, Pencil, Trash2, ArrowRight } from "lucide-react";

import { API_URL as API } from "@/lib/api";

const ACTION_CONFIG = {
  crear: { label: "Creado", icon: Plus, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  editar: { label: "Editado", icon: Pencil, color: "bg-amber-50 text-amber-700 border-amber-200" },
  eliminar: { label: "Eliminado", icon: Trash2, color: "bg-rose-50 text-rose-700 border-rose-200" },
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("es-BO", { day: "2-digit", month: "short", year: "numeric" }) +
    " " + d.toLocaleTimeString("es-BO", { hour: "2-digit", minute: "2-digit" });
};

const ChangeDetails = ({ details, action }) => {
  if (action === "editar" && details?.cambios) {
    const entries = Object.entries(details.cambios);
    if (entries.length === 0) return <span className="text-muted-foreground text-xs">Sin cambios</span>;
    return (
      <div className="space-y-1">
        {entries.map(([field, val]) => (
          <div key={field} className="flex items-center gap-1.5 text-xs">
            <span className="font-medium text-foreground min-w-[70px]">{field}:</span>
            <span className="text-rose-600 line-through">{String(val.antes ?? "-")}</span>
            <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
            <span className="text-emerald-700 font-medium">{String(val.despues ?? "-")}</span>
          </div>
        ))}
      </div>
    );
  }
  if (action === "crear" && details?.producto) {
    const p = details.producto;
    return (
      <span className="text-xs text-muted-foreground">
        {p.descripcion} | {p.familia} | Stock: {p.stock} | Bs {p.pmenor}
      </span>
    );
  }
  if (action === "eliminar" && details?.producto_eliminado) {
    const p = details.producto_eliminado;
    return (
      <span className="text-xs text-muted-foreground">
        {p.descripcion || "-"} | {p.familia || "-"} | Stock: {p.stock ?? "-"}
      </span>
    );
  }
  return <span className="text-muted-foreground text-xs">-</span>;
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action: "", codigo: "", dateFrom: "", dateTo: "" });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", 30);
      if (filters.action) params.append("action", filters.action);
      if (filters.codigo) params.append("codigo", filters.codigo);
      if (filters.dateFrom) params.append("date_from", filters.dateFrom);
      if (filters.dateTo) params.append("date_to", filters.dateTo);
      const { data } = await axios.get(`${API}/api/audit-log?${params}`, { withCredentials: true });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const resetFilters = () => {
    setFilters({ action: "", codigo: "", dateFrom: "", dateTo: "" });
    setPage(1);
  };

  return (
    <div className="space-y-5" data-testid="audit-log-page">
      <div className="flex items-center gap-3">
        <div className="bg-[#232F3E] p-2 rounded-lg">
          <History className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Historial de Cambios</h2>
          <p className="text-sm text-muted-foreground">{total} registros de actividad CRUD</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 border border-border">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1 min-w-[140px]">
            <label className="text-xs font-medium text-muted-foreground">Accion</label>
            <Select value={filters.action || "__all__"} onValueChange={(v) => { setFilters({ ...filters, action: v === "__all__" ? "" : v }); setPage(1); }}>
              <SelectTrigger data-testid="audit-filter-action" className="h-9 text-sm">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas</SelectItem>
                <SelectItem value="crear">Creado</SelectItem>
                <SelectItem value="editar">Editado</SelectItem>
                <SelectItem value="eliminar">Eliminado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 min-w-[180px]">
            <label className="text-xs font-medium text-muted-foreground">Codigo</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                data-testid="audit-filter-codigo"
                className="h-9 pl-8 text-sm"
                placeholder="Buscar codigo..."
                value={filters.codigo}
                onChange={(e) => { setFilters({ ...filters, codigo: e.target.value }); setPage(1); }}
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Desde</label>
            <Input
              data-testid="audit-filter-from"
              type="date"
              className="h-9 text-sm"
              value={filters.dateFrom}
              onChange={(e) => { setFilters({ ...filters, dateFrom: e.target.value }); setPage(1); }}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Hasta</label>
            <Input
              data-testid="audit-filter-to"
              type="date"
              className="h-9 text-sm"
              value={filters.dateTo}
              onChange={(e) => { setFilters({ ...filters, dateTo: e.target.value }); setPage(1); }}
            />
          </div>
          {(filters.action || filters.codigo || filters.dateFrom || filters.dateTo) && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs text-muted-foreground">
              Limpiar filtros
            </Button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted">
                <TableHead className="font-semibold text-xs w-[140px]">Fecha</TableHead>
                <TableHead className="font-semibold text-xs w-[100px]">Accion</TableHead>
                <TableHead className="font-semibold text-xs w-[120px]">Codigo</TableHead>
                <TableHead className="font-semibold text-xs">Usuario</TableHead>
                <TableHead className="font-semibold text-xs">Detalles</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Cargando historial...</TableCell></TableRow>
              ) : logs.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Sin registros de actividad</TableCell></TableRow>
              ) : (
                logs.map((log) => {
                  const ac = ACTION_CONFIG[log.action] || ACTION_CONFIG.editar;
                  const ActionIcon = ac.icon;
                  return (
                    <TableRow key={log._id} data-testid={`audit-row-${log._id}`}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(log.timestamp)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${ac.color} gap-1 text-xs`}>
                          <ActionIcon className="w-3 h-3" />
                          {ac.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs font-semibold text-foreground">{log.codigo}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{log.user_name || log.user_email}</TableCell>
                      <TableCell className="max-w-[400px]">
                        <ChangeDetails details={log.details} action={log.action} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Pagina {page} de {pages} ({total} registros)</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="audit-next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
